import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Download } from "lucide-react";
import { api, fmtDate, money } from "@/lib/api";
import { downloadInvoicePdf } from "@/components/MyLedger";
import { Spinner, SEO } from "@/components/Shared";

export default function Invoice() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);

  useEffect(() => { api.get(`/orders/${id}/invoice`).then(({ data }) => setInv(data)).catch(() => setInv(false)); }, [id]);

  if (inv === null) return <Spinner label="Loading invoice" />;
  if (inv === false) return <p className="p-10 text-center text-sm text-slate-500" data-testid="invoice-error">We couldn't find that invoice.</p>;

  const paid = inv.status === "paid";
  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10 pb-28" data-testid="invoice-page">
      <SEO title={inv.invoice_no} description="Buddilio invoice" />
      <img src="/brand/lockup.png" alt="Buddilio" data-testid="invoice-logo"
        className="mb-6 h-14 w-auto" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="overline" data-testid="invoice-template">{inv.template || (paid ? "Receipt" : "Invoice")}</p>
          <h1 className="mt-2 text-3xl font-bold" data-testid="invoice-number">{paid ? inv.receipt_no : inv.invoice_no}</h1>
          <p className="mt-1 text-sm text-slate-500">Issued {fmtDate(inv.issued_at)} · {inv.kind_label}</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <button onClick={() => downloadInvoicePdf(id, inv.receipt_no || inv.invoice_no)} data-testid="invoice-download-pdf"
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-5 py-2.5 text-xs font-bold text-white">
            <Download className="h-3.5 w-3.5" />Download {paid ? "receipt" : "invoice"} PDF
          </button>
          <button onClick={() => window.print()} data-testid="invoice-print"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-5 py-2.5 text-xs font-bold">
            <Printer className="h-3.5 w-3.5" />Print
          </button>
        </div>
      </div>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-400">From</p>
          <p className="mt-1 font-bold">{inv.seller.name}</p>
          <p className="text-sm text-slate-500">{inv.seller.site}<br />{inv.seller.email}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-widest text-slate-400">Billed to</p>
          <p className="mt-1 font-bold" data-testid="invoice-buyer">{inv.buyer.name}</p>
          <p className="text-sm text-slate-500">{inv.buyer.email}<br />{[inv.buyer.city, inv.buyer.country].filter(Boolean).join(", ")}</p>
        </div>
      </div>

      <table className="mt-8 w-full text-sm">
        <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr><th className="py-2">Description</th><th className="py-2 text-right">Qty</th><th className="py-2 text-right">Amount</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100" data-testid="invoice-lines">
          {inv.lines.map((l, i) => (
            <tr key={i}><td className="py-3">{l.description}</td><td className="py-3 text-right">{l.quantity}</td>
              <td className="py-3 text-right">{money(l.amount)}</td></tr>
          ))}
        </tbody>
      </table>

      <div className="mt-6 ml-auto max-w-xs space-y-1.5 text-sm">
        {inv.discount > 0 && <Row label="Discount" value={`− ${money(inv.discount)}`} />}
        {inv.credit_applied > 0 && <Row label="Buddilio credit" value={`− ${money(inv.credit_applied)}`} />}
        <Row label={`${inv.tax_label}${inv.tax_percent ? ` (${inv.tax_percent}%)` : ""}`} value={money(inv.tax)} />
        <div className="flex items-center justify-between border-t border-slate-200 pt-2 text-base font-bold">
          <span>Total</span><span data-testid="invoice-total">{money(inv.total)}</span>
        </div>
        <p className="pt-1 text-xs text-slate-500" data-testid="invoice-status">
          {paid ? `Paid via ${inv.gateway || "Buddilio"}${inv.transaction_id ? ` · ${inv.transaction_id}` : ""}` : `Status: ${inv.status}`}
          {inv.refund_status !== "none" ? ` · refund ${inv.refund_status}` : ""}
        </p>
      </div>

      {inv.note && <p className="mt-10 text-xs text-slate-500" data-testid="invoice-note">{inv.note}</p>}
      <p className="mt-3 text-[11px] text-slate-400">
        This document is generated by Buddilio for the transaction above.
      </p>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between"><span className="text-slate-500">{label}</span><span>{value}</span></div>
);
